import { ActionIcon, Box, PinInput, Text } from "@mantine/core";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import type { CSSProperties, ReactNode } from "react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import classes from "./CodeInput.module.css";

export interface CodeInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: ReactNode;
  length?: number;
  masked?: boolean;
  /** Шошгоны хажууд нүд харуулах/нуух товч (canvas 3.3 ПИН). */
  allowReveal?: boolean;
  oneTimeCode?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  /** lg = хуудасны код (52 px, canvas 1.6 Forgot2); md = цонхны ПИН (46 px, canvas 3.3 PinDialogs). */
  size?: "md" | "lg";
}

/** Шошго, тэнцүү N нүд, алдаа нүдний доор. CodeField ба PinCells-ийн нэгдсэн хувилбар. */
export function CodeInput({
  label,
  value,
  onChange,
  error,
  length = 6,
  masked = false,
  allowReveal = false,
  oneTimeCode = false,
  autoFocus = false,
  disabled = false,
  size = "lg",
}: CodeInputProps) {
  const { t } = useTranslation("profileForm");
  const labelId = useId();
  const errorId = useId();
  const [revealed, setRevealed] = useState(false);
  const vars = { "--code-length": length } as CSSProperties;

  return (
    <Box role="group" aria-labelledby={labelId} className={classes.root} data-size={size} style={vars}>
      <Box className={classes.head}>
        <Text id={labelId} className={classes.label}>
          {label}
        </Text>
        {allowReveal && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            radius="md"
            disabled={disabled}
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? t("dialog.hidePin") : t("dialog.showPin")}
            aria-pressed={revealed}
          >
            {revealed ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
          </ActionIcon>
        )}
      </Box>
      <PinInput
        length={length}
        type="number"
        inputMode="numeric"
        mask={masked && !revealed}
        value={value}
        onChange={onChange}
        error={!!error}
        oneTimeCode={oneTimeCode}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder=""
        classNames={{ root: classes.cells, pinInput: classes.cell, input: classes.input }}
        getInputProps={(index) => ({
          // Modal's focus trap otherwise lands on the close button (canvas: first cell).
          "data-autofocus": autoFocus && index === 0 ? true : undefined,
          "aria-label": t("dialog.digit", { n: index + 1 }),
          "aria-describedby": error ? errorId : undefined,
        })}
      />
      {error && (
        <Text id={errorId} className={classes.error}>
          {error}
        </Text>
      )}
    </Box>
  );
}
